"use client";

import { useEffect, useRef, useState } from "react";
import { useGetSyncJobStatus } from "@/lib/api/generated";
import type { SyncJobResponse } from "@/lib/api/model";

export const SYNC_JOB_TERMINAL_STATUSES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
]);

/** Surface "still starting…" when the job stays queued this long. */
export const SYNC_JOB_STUCK_QUEUED_MS = 30_000;

/**
 * Soft client timeout — stop polling and warn; do **not** cancel server work.
 * Spec: ~10–15 min; plan: ~12 min.
 */
export const SYNC_JOB_SOFT_TIMEOUT_MS = 12 * 60 * 1000;

const POLL_INITIAL_MS = 1_000;
const POLL_MID_MS = 3_000;
const POLL_MAX_MS = 8_000;

export function isSyncJobTerminalStatus(status: string | undefined): boolean {
  return !!status && SYNC_JOB_TERMINAL_STATUSES.has(status);
}

/** ~1s → backoff to 3s → 8s while the job is still in flight. */
export function syncJobPollIntervalMs(elapsedMs: number): number {
  if (elapsedMs < 4_000) return POLL_INITIAL_MS;
  if (elapsedMs < 12_000) return POLL_MID_MS;
  return POLL_MAX_MS;
}

export type UseSyncJobPollResult = {
  data: SyncJobResponse | undefined;
  isTerminal: boolean;
  stuckStarting: boolean;
  timedOut: boolean;
  isPolling: boolean;
  isError: boolean;
};

/**
 * Polls GET sync-job status while `queued|running`, stops on terminal
 * statuses, surfaces stuck-starting and soft-timeout flags.
 */
export function useSyncJobPoll({
  jobId,
  syncJobId,
  enabled = true,
  /** Wall-clock start (e.g. accept time) so refresh keeps soft-timeout budget. */
  startedAtMs,
}: {
  jobId: string;
  syncJobId: string | null;
  enabled?: boolean;
  startedAtMs?: number;
}): UseSyncJobPollResult {
  const active = enabled && !!jobId && !!syncJobId;
  const startedAtRef = useRef(startedAtMs ?? Date.now());
  const [stuckStarting, setStuckStarting] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!active) {
      setStuckStarting(false);
      setTimedOut(false);
      return;
    }
    const start = startedAtMs ?? Date.now();
    startedAtRef.current = start;
    const elapsed = Date.now() - start;
    setStuckStarting(elapsed >= SYNC_JOB_STUCK_QUEUED_MS);
    setTimedOut(elapsed >= SYNC_JOB_SOFT_TIMEOUT_MS);

    const stuckTimer = setTimeout(() => {
      setStuckStarting(true);
    }, Math.max(0, SYNC_JOB_STUCK_QUEUED_MS - elapsed));

    const softTimer = setTimeout(() => {
      setTimedOut(true);
    }, Math.max(0, SYNC_JOB_SOFT_TIMEOUT_MS - elapsed));

    return () => {
      clearTimeout(stuckTimer);
      clearTimeout(softTimer);
    };
  }, [active, jobId, syncJobId, startedAtMs]);

  const query = useGetSyncJobStatus(jobId, syncJobId ?? "", {
    query: {
      enabled: active && !timedOut,
      staleTime: 0,
      // A single transient status-poll failure shouldn't tear down the
      // "Syncing…" toast — retry a few times before surfacing isError.
      retry: 2,
      refetchInterval: (q) => {
        if (timedOut) return false;
        const status = q.state.data?.status;
        if (isSyncJobTerminalStatus(status)) return false;
        const elapsed = Date.now() - startedAtRef.current;
        if (elapsed >= SYNC_JOB_SOFT_TIMEOUT_MS) return false;
        return syncJobPollIntervalMs(elapsed);
      },
    },
  });

  const status = query.data?.status;
  const isTerminal = isSyncJobTerminalStatus(status);

  // Once we leave queued, clear stuckStarting even if the 30s timer already fired.
  const effectiveStuckStarting =
    stuckStarting && !isTerminal && status === "queued";

  // Soft timeout only applies while still in-flight (and never after a terminal).
  const effectiveTimedOut = timedOut && !isTerminal;

  return {
    data: query.data,
    isTerminal,
    stuckStarting: effectiveStuckStarting,
    timedOut: effectiveTimedOut,
    isPolling: active && !isTerminal && !effectiveTimedOut,
    isError: query.isError,
  };
}
